import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, todayISO, formatDateTime } from "../lib/api";
import { toast } from "sonner";
import { Calendar, Save, BarChart3, Users, UserCheck } from "lucide-react";
import { EmployeeAttendanceHistory } from "../components/EmployeeAttendanceHistory";

const STATUS_CHOICES = [
  { value: "present", label: "P", title: "Present", color: "bg-[#15803D] text-white border-[#15803D]" },
  { value: "half-day", label: "H", title: "Half-day", color: "bg-[#B45309] text-white border-[#B45309]" },
  { value: "absent", label: "A", title: "Absent", color: "bg-[#B91C1C] text-white border-[#B91C1C]" },
];

export default function Attendance() {
  const [date, setDate] = useState(todayISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [historyId, setHistoryId] = useState("");

  const load = async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/attendance?date=${d}`);
      setRows(data.rows || []);
      setSavedCount(data.saved_count || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(date); }, [date]);

  const setStatus = (eid, status) => {
    setRows(rows.map((r) => (r.employee_id === eid ? { ...r, status } : r)));
  };

  const counts = useMemo(() => {
    return {
      present: rows.filter((r) => r.status === "present").length,
      half: rows.filter((r) => r.status === "half-day").length,
      absent: rows.filter((r) => r.status === "absent").length,
      unmarked: rows.filter((r) => !r.status).length,
    };
  }, [rows]);

  const markAll = (status) => setRows(rows.map((r) => ({ ...r, status })));

  const save = async () => {
    const filled = rows.filter((r) => r.status);
    if (filled.length === 0) {
      toast.error("Mark at least one employee before saving");
      return;
    }
    setBusy(true);
    try {
      await api.post("/attendance", {
        date,
        rows: filled.map((r) => ({ employee_id: r.employee_id, status: r.status, notes: r.notes || "" })),
      });
      toast.success(`Saved ${filled.length} attendance record${filled.length === 1 ? "" : "s"}`);
      setSavedCount(filled.length);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Calendar size={26} strokeWidth={2.5} />
          <div>
            <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">Attendance</h1>
            <p className="text-xs text-gray-500">Mark each employee for the selected date</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/attendance/summary" className="h-10 px-4 border border-gray-300 rounded-sm font-semibold text-sm inline-flex items-center gap-2 hover:bg-gray-50" data-testid="goto-summary">
            <BarChart3 size={16} /> Monthly Summary
          </Link>
        </div>
      </div>

      <div className="card-flat p-4 flex flex-wrap items-center gap-3">
        <label className="small-label" htmlFor="emp-history-select">View employee attendance</label>
        <select id="emp-history-select" className="input-lg max-w-xs" value={historyId}
          onChange={(e) => setHistoryId(e.target.value)} data-testid="emp-history-select">
          <option value="">Select employee…</option>
          {rows.map((r) => <option key={r.employee_id} value={r.employee_id}>{r.name}</option>)}
        </select>
      </div>

      {historyId && <EmployeeAttendanceHistory employeeId={historyId} onClose={() => setHistoryId("")} />}

      <div className="card-flat p-5 grid grid-cols-1 sm:grid-cols-5 gap-4">
        <div className="sm:col-span-2">
          <label className="small-label block mb-1">Date</label>
          <input type="date" className="input-lg" value={date} onChange={(e) => setDate(e.target.value)} max={todayISO()} data-testid="attendance-date" />
          {savedCount > 0 && (
            <div className="text-xs text-[#15803D] font-semibold mt-1.5">
              {savedCount} record{savedCount === 1 ? "" : "s"} already saved for this date
            </div>
          )}
        </div>
        <div className="card-flat p-3 sm:col-span-3 grid grid-cols-4 gap-2">
          <div>
            <div className="small-label text-gray-500">Present</div>
            <div className="data-num text-2xl text-[#15803D]" data-testid="count-present">{counts.present}</div>
          </div>
          <div>
            <div className="small-label text-gray-500">Half</div>
            <div className="data-num text-2xl text-[#B45309]" data-testid="count-half">{counts.half}</div>
          </div>
          <div>
            <div className="small-label text-gray-500">Absent</div>
            <div className="data-num text-2xl text-[#B91C1C]" data-testid="count-absent">{counts.absent}</div>
          </div>
          <div>
            <div className="small-label text-gray-500">Unmarked</div>
            <div className="data-num text-2xl text-gray-400" data-testid="count-unmarked">{counts.unmarked}</div>
          </div>
        </div>
      </div>

      <div className="card-flat">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-gray-600">{rows.length} active employee{rows.length === 1 ? "" : "s"}</div>
          {rows.length > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-500">Quick mark all:</span>
              <button onClick={() => markAll("present")} className="px-2 py-1 border border-[#15803D] text-[#15803D] font-bold rounded-sm hover:bg-[#15803D] hover:text-white">All P</button>
              <button onClick={() => markAll("absent")} className="px-2 py-1 border border-[#B91C1C] text-[#B91C1C] font-bold rounded-sm hover:bg-[#B91C1C] hover:text-white">All A</button>
            </div>
          )}
        </div>

        {loading && <div className="p-8 text-center text-gray-400 text-sm">Loading employees…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center text-gray-500 text-sm">
            No active employees yet. Add some from <Link className="underline font-semibold" to="/employees">Employees</Link>.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <ul className="divide-y divide-gray-200">
            {rows.map((r) => (
              <li key={r.employee_id} className="p-4 flex items-center justify-between gap-4 hover:bg-gray-50" data-testid={`att-row-${r.employee_id}`}>
                <div className="min-w-0 flex-1">
                  <button type="button" onClick={() => { setHistoryId(r.employee_id); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className="font-semibold truncate hover:underline text-left" data-testid={`att-name-${r.employee_id}`}>{r.name}</button>
                  <div className="text-xs text-gray-500 truncate">
                    {r.role || "—"} · {r.wage_type === "monthly" ? "Monthly" : "Daily wage"}
                  </div>
                  {r.marked_by && (
                    <div className="text-[10px] text-gray-400 mt-0.5 inline-flex items-center gap-1" data-testid={`att-marked-by-${r.employee_id}`}>
                      <UserCheck size={10} />
                      Marked by {r.marked_by}
                      {r.marked_at ? ` · ${formatDateTime(r.marked_at)}` : ""}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {STATUS_CHOICES.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => setStatus(r.employee_id, s.value)}
                      title={s.title}
                      data-testid={`mark-${r.employee_id}-${s.value}`}
                      className={`w-10 h-10 rounded-sm border-2 font-heading font-black text-base transition ${r.status === s.value ? s.color : "bg-white border-gray-300 text-gray-400 hover:border-gray-500"}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {rows.length > 0 && (
        <div className="sticky bottom-0 lg:bottom-2 z-20 flex justify-end">
          <button onClick={save} disabled={busy} className="btn-primary h-12 px-6 flex items-center gap-2 shadow-lg" data-testid="save-attendance">
            <Save size={18} /> {busy ? "Saving…" : `Save Attendance for ${date}`}
          </button>
        </div>
      )}
    </div>
  );
}
